import { Dataset, DatasetItem } from '../../../types/types';
import {
  DEFAULT_LOGO_HEIGHT_MM,
  DEFAULT_PAGE_SETUP,
  PORTRAIT_WIDTH_BUDGET_EM,
  SYSTEM_TEMPLATE_ID_SUFFIX,
  TAX_GROSS_ITEM_NAMES,
  TAX_NET_ITEM_NAMES,
  TAX_RATE_ITEM_NAMES,
} from '../constants';
import {
  ReportBlock,
  ReportColumn,
  ReportLeafBlock,
  ReportOrientation,
  ReportTemplate,
} from '../types';
import { isTechnicalItem } from './classifyDatasetItem';
import { findFirstItemName } from './findFirstItemName';
import { isNumericItemType } from './isNumericItemType';
import { resolveColumnWidthEm } from './resolveColumnWidthEm';

/** Builds the system default template id for a dataset. */
export const getDefaultTemplateId = (datasetId: string): string =>
  `${datasetId}.${SYSTEM_TEMPLATE_ID_SUFFIX}`;

/**
 * Columns of the generated table: every column the grid shows, in grid order.
 * A string `visible` is a JSONata expression that needs a row to evaluate, so
 * it counts as visible here — the same rule resolveExportColumns applies.
 */
const buildColumns = (items: DatasetItem[]): ReportColumn[] =>
  items
    // Technical columns (ids, foreign keys, sync timestamps, ordering counters)
    // carry no meaning on paper even when the grid happens to show them.
    .filter(
      (item) =>
        Boolean(item.name) &&
        item.printVisible !== false &&
        (item.printVisible === true || item.visible !== false) &&
        !isTechnicalItem(item)
    )
    .map((item) => ({
      item: item.name,
      width: resolveColumnWidthEm(item),
      align:
        item.alignment ?? (isNumericItemType(item.type) ? 'right' : 'left'),
    }));

/** Wide tables do not fit portrait; pick the orientation from the total width. */
const resolveOrientation = (columns: ReportColumn[]): ReportOrientation => {
  const totalWidth = columns.reduce(
    (total, column) => total + (column.width ?? 0),
    0
  );

  return totalWidth > PORTRAIT_WIDTH_BUDGET_EM ? 'landscape' : 'portrait';
};

const buildHeaderIdentityBlocks = (datasetId: string): ReportLeafBlock[] => [
  {
    kind: 'logo',
    heightMm: DEFAULT_LOGO_HEIGHT_MM,
    align: 'left',
  },
  ...(datasetId === 'salesJournal'
    ? [
        {
          kind: 'fields' as const,
          columns: 1,
          fields: [
            { item: 'companyName' },
            { item: 'venueName' },
            { item: 'printedBy' },
          ],
        },
      ]
    : []),
];

/**
 * Footer aggregates: the row count plus the definition-selected operation for
 * each numeric column. Columns default to sum, while ratios, rates and unit
 * prices can explicitly opt out or request an average. The identifier column
 * is always excluded because aggregating ids is never meaningful.
 */
const buildFooterBlocks = (
  dataset: Dataset,
  items: DatasetItem[],
  source: 'master'
): ReportBlock[] => {
  const blocks: ReportBlock[] =
    dataset.printRowCount === false
      ? []
      : [{ kind: 'meta', field: 'rowCount' }];

  items
    .filter(
      (item) =>
        Boolean(item.name) &&
        item.printVisible !== false &&
        (item.printVisible === true || item.visible !== false) &&
        isNumericItemType(item.type) &&
        !isTechnicalItem(item) &&
        item.reportAggregate !== false &&
        item.name !== dataset.idItem
    )
    .forEach((item) => {
      if (item.reportAggregate === false) return;
      blocks.push({
        kind: 'aggregate',
        label: item.label ?? item.name,
        operation: item.reportAggregate ?? 'sum',
        item: item.name,
        source,
      });
    });

  const rateItem = findFirstItemName(items, TAX_RATE_ITEM_NAMES);
  const netItem = findFirstItemName(items, TAX_NET_ITEM_NAMES);
  const grossItem = findFirstItemName(items, TAX_GROSS_ITEM_NAMES);

  // Only add the breakdown when all three inputs exist; otherwise it cannot be
  // computed and an empty block would just be noise.
  const isNumericReference = (itemName: string | undefined): boolean =>
    Boolean(
      itemName &&
      isNumericItemType(items.find((item) => item.name === itemName)?.type)
    );

  if (
    rateItem &&
    netItem &&
    grossItem &&
    [rateItem, netItem, grossItem].every(isNumericReference)
  ) {
    blocks.push({
      kind: 'taxBreakdown',
      label: 'print.taxBreakdown',
      source,
      groupBy: rateItem,
      net: netItem,
      gross: grossItem,
      taxFrom: 'difference',
    });
  }

  return blocks;
};

/**
 * Generates the system default report for any dataset straight from its
 * definition. One generator covers every shipped dataset (and any added later),
 * which is why no per-dataset default files are needed.
 */
export const buildDefaultReportTemplate = (
  dataset: Dataset
): ReportTemplate => {
  const items: DatasetItem[] = Array.isArray(dataset.items)
    ? dataset.items
    : [];
  const columns = buildColumns(items);
  const datasetId = String(dataset.datasetId ?? '');

  return {
    version: 1,
    templateId: getDefaultTemplateId(datasetId),
    datasetId,
    label: dataset.label,
    isSystemDefault: true,
    page: {
      ...DEFAULT_PAGE_SETUP,
      orientation: resolveOrientation(columns),
    },
    sections: {
      header: {
        source: 'master',
        blocks: [
          // Logo and print metadata share one grid row, the way a document
          // header is normally laid out — and it is the same grid the designer
          // snaps dropped elements to.
          {
            kind: 'row',
            cells: [
              {
                span: 7,
                blocks: buildHeaderIdentityBlocks(datasetId),
              },
              {
                span: 5,
                align: 'right',
                blocks: [
                  { kind: 'meta', field: 'printedAt' },
                  { kind: 'meta', field: 'filters' },
                ],
              },
            ],
          },
          {
            kind: 'meta',
            field: 'reportTitle',
            style: { fontSizePt: 14, bold: true },
          },
        ],
      },
      items: {
        source: 'master',
        columns,
        repeatHeaderOnEachPage: true,
        gridLines: { vertical: true, horizontal: true },
      },
      footer: {
        blocks: buildFooterBlocks(dataset, items, 'master'),
      },
    },
  };
};
